// SPA router on the Navigation API (window.navigation). In-app <a href> clicks,
// browser back/forward, and navigation.navigate() all surface as one `navigate`
// event, so we never wire click handlers or touch history plumbing: we decide per
// event whether to event.intercept({ handler }) and render. Deep links resolve
// through Vite's SPA fallback to index.html, then render here on first paint.

// ctx.signal (RouteContext below) is a per-view lifetime token: it aborts when a
// newer navigation supersedes this one OR when the next view commits (navigate-away).
// Hang view teardown (store unsubscribe, delegated listeners, in-flight fetches) off it.

import { el } from "../lib/dom.js";

// Order matters: first match wins. Each load is a dynamic import, so a view's module is
// only fetched the first time its route is visited.
const ROUTES = [
  { path: "/", load: () => import("../views/schedule-view.js") },
  { path: "/session/:id", load: () => import("../views/session-view.js") },
  { path: "/agenda", load: () => import("../views/agenda-view.js") },
  { path: "/about", load: () => import("../views/about-view.jsx") },
];

// Rendered when no route matches (an in-app 404). Not a pattern entry: the absence of
// a match, not a path.
const NOT_FOUND = { load: () => import("../views/not-found-view.js") };

function compile(path) {
  const keys = [];
  const source = path
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // escape regex metachars
    .replace(/:([A-Za-z0-9_]+)/g, (_match, key) => {
      keys.push(key);
      return "([^/]+)";
    });
  return { pattern: new RegExp(`^${source}/?$`), keys };
}

const compiledRoutes = ROUTES.map(({ path, load }) => ({ ...compile(path), load }));

function matchRoute(pathname) {
  for (const route of compiledRoutes) {
    const result = route.pattern.exec(pathname);
    if (!result) {
      continue;
    }
    const params = {};
    route.keys.forEach((key, index) => {
      params[key] = decodeURIComponent(result[index + 1] ?? "");
    });
    return { load: route.load, params };
  }
  return null;
}

function resolveTitle(title, ctx) {
  return typeof title === "function" ? title(ctx) : title;
}

function getMount() {
  const main = document.getElementById("main");
  if (!(main instanceof HTMLElement)) {
    throw new Error('Router: missing mount point <main id="main">.');
  }
  return main;
}

// Reflect the active route on the primary nav (aria-current="page").
function updateActiveNav(pathname) {
  const links = document.querySelectorAll("[data-nav-link]");
  for (const link of links) {
    if (!(link instanceof HTMLAnchorElement)) {
      continue;
    }
    const linkPath = new URL(link.href).pathname;
    const isActive = linkPath === "/" ? pathname === "/" : pathname.startsWith(linkPath);
    if (isActive) {
      link.setAttribute("aria-current", "page");
    } else {
      link.removeAttribute("aria-current");
    }
  }
}

// Move focus to the view's <h1> so screen-reader and keyboard users land on the new
// content (the Navigation API does not do this for us).
function focusHeading(mount) {
  const heading = mount.querySelector("h1");
  if (!heading) {
    return;
  }
  heading.setAttribute("tabindex", "-1");
  heading.focus();
}

// The controller whose signal backs the current view's ctx.signal. Aborted when the
// next view commits, and that abort runs the outgoing view's teardown (store
// unsubscribe, delegated favorite-change listener) on navigate-away, which the
// Navigation API's own per-navigation signal does NOT do. Module-scoped: exactly one
// current view at a time.
let currentViewController = null;

// Aborting the outgoing controller fires every teardown keyed off its signal.
function commitView(controller) {
  currentViewController?.abort();
  currentViewController = controller;
}

// Abort target when source aborts (or immediately, if it already has), so a newer
// navigation's cancellation of source also cancels this view's in-flight work and
// tears down anything it subscribed before being superseded.
function linkAbort(source, target) {
  if (source.aborted) {
    target.abort();
    return;
  }
  source.addEventListener("abort", () => target.abort(), { once: true });
}

// Exported (named) only for the router-resilience unit test, which stubs a rejecting
// route module and asserts the fallback renders and aria-busy clears. Not public
// surface: main.js only calls startRouter. Focus and scroll differ by navigationType;
// see the inline notes at the swap.
export async function renderRoute(url, signal, options = {}) {
  const mount = getMount();
  const match = matchRoute(url.pathname);
  const load = match ? match.load : NOT_FOUND.load;
  const params = match ? match.params : {};

  // Per-view lifetime token handed to the view as ctx.signal. commitView aborts it when
  // the NEXT view commits; we compose the incoming (Navigation API) signal so a newer
  // navigation still cancels this view's in-flight load/mount.
  const viewController = new AbortController();
  linkAbort(signal, viewController);

  mount.setAttribute("aria-busy", "true");
  try {
    const module = await load();
    if (signal.aborted) {
      return;
    }

    const ctx = { params, url, signal: viewController.signal };
    const node = await module.render(ctx);
    if (signal.aborted) {
      return;
    }

    // Stamp the route's static title now, before the swap. An async view (e.g. session
    // detail) sets its REAL title from inside its own load() once the fetch resolves;
    // stamping here first makes that later set the unconditional last writer. The
    // aborted guards above mean we never title a superseded navigation.
    document.title = resolveTitle(module.title, ctx);

    // Swap the view in. Scroll is the platform's job: the Navigation API default
    // (scroll: "after-transition") restores the saved offset on a traverse and resets to
    // the top on a push once this handler resolves, so there's nothing to do here.
    const isTraverse = options.navigationType === "traverse";
    mount.replaceChildren(node);
    // The incoming view is now in the DOM: retire the one it replaced (aborting its
    // ctx.signal, which triggers store unsubscribe + delegated-listener removal) and
    // become the current view. Done at the swap, not before, so a superseded render that
    // never committed can't tear down the view the user is still looking at.
    commitView(viewController);
    mount.removeAttribute("aria-busy");
    // A route rendered cleanly: tear down any pending offline-recovery listener so it
    // can't later reload a page the user is now happily viewing.
    clearRouteRecovery();
    updateActiveNav(url.pathname);

    // Move focus to the heading on a fresh landing (push/replace), but NOT on a traverse:
    // a focus() would scroll the heading into view and fight the platform's scroll
    // restore, so back/forward keeps the browser's native focus.
    if (!options.initial && !isTraverse) {
      focusHeading(mount);
    }
  } catch (error) {
    // A newer navigation superseding this one is not a real failure.
    if (signal.aborted) {
      return;
    }
    // The Navigation API commits the URL synchronously at event.intercept(), so without
    // this catch a rejected load() (a dynamic import() that fails to fetch its chunk)
    // would leave the URL changed, the OLD view still mounted, and aria-busy stuck
    // "true": a silent freeze. Instead, render a visible recoverable fallback so the
    // user isn't stranded.
    renderRouteError(mount, url, error, viewController);
  }
}

// Cancels a pending reconnect-reload listener when one is armed. Module-scoped so a
// clean navigation or a fresh failure supersedes a stale listener instead of leaking
// one that would reload a page the user is happily viewing.
let recoverController = null;

// Tear down any armed reconnect-recovery.
function clearRouteRecovery() {
  recoverController?.abort();
  recoverController = null;
}

// Render a visible, recoverable fallback into <main> when a route fails to load or
// render: clears aria-busy, restores an <h1> with focus, and offers a retry.
//
// The retry is a FULL RELOAD, not a re-import(): a dynamic import() that REJECTS is
// memoized by the browser's module map (Chromium and Firefox both replay the rejection
// on a same-specifier re-import without a fresh request), so re-navigating in place
// would just re-freeze. A reload tears down the module map and re-imports the chunk
// cleanly. Same remedy Vite recommends for its vite:preloadError.
//
// Offline, we also reload automatically the moment the network returns.
function renderRouteError(mount, url, error, viewController) {
  console.error(`Router: failed to render ${url.pathname}`, error);

  const online = navigator.onLine;
  const eyebrow = online ? "Couldn’t load" : "Offline";
  const heading = online ? "This page didn’t load" : "This page isn’t available offline yet";
  const message = online
    ? `We couldn’t load ${url.pathname}. Try again to reload the page.`
    : `We couldn’t load ${url.pathname} and there’s no network right now. ` +
      "It’ll retry automatically as soon as you’re back online.";

  const retry = el("button", { type: "button", class: "pill", "data-variant": "primary" }, "Try again");
  retry.addEventListener("click", () => window.location.reload());

  const node = el(
    "div",
    { class: "container" },
    el(
      "div",
      { class: "empty-state", role: "alert" },
      el("p", { class: "eyebrow" }, eyebrow),
      el("h1", { tabindex: "-1" }, heading),
      el("p", null, message),
      retry,
    ),
  );

  mount.replaceChildren(node);
  // The failed view has left the DOM: retire it (tear down its subscriptions) and make
  // this fallback the current view.
  commitView(viewController);
  mount.removeAttribute("aria-busy");
  document.title = online ? "Couldn’t load · DevConf Companion" : "Offline · DevConf Companion";
  focusHeading(mount);

  // Offline: reload once the network is back. One-shot, cancelled by the next clean
  // render so it never reloads a page the user has since navigated to.
  if (!online) {
    recoverController?.abort();
    recoverController = new AbortController();
    window.addEventListener("online", () => window.location.reload(), { once: true, signal: recoverController.signal });
  }
}

function shouldIntercept(event) {
  if (!event.canIntercept) {
    return false;
  }
  if (event.hashChange) {
    return false;
  }
  if (event.downloadRequest !== null) {
    return false;
  }
  if (event.formData) {
    return false;
  }
  return true;
}

function startNavigationApi() {
  const navigation = window.navigation;
  if (!navigation) {
    throw new Error("Router: this app requires the Navigation API (use a current Chromium, Firefox, or Safari).");
  }

  // Safety net: renderRoute already catches load/render rejections and shows a fallback
  // (so the handler resolves and this rarely fires), but a failed navigation must never
  // be swallowed, e.g. a synchronous throw before the handler runs.
  navigation.addEventListener("navigateerror", (event) => {
    console.error("Router: navigation failed",  (event).error);
  });

  navigation.addEventListener("navigate", (event) => {
    if (!shouldIntercept(event)) {
      return;
    }
    const url = new URL(event.destination.url);

    event.intercept({
      // We manage focus ourselves (move it to the view's <h1>) for a11y, so opt out of
      // the platform's focus reset. Scroll we leave to the platform default.
      focusReset: "manual",
      handler: async () => {
        await renderRoute(url, event.signal, { navigationType: event.navigationType });
      },
    });
  });

  // First paint: the Navigation API does not fire `navigate` for the initial document
  // load, so render the current URL directly (no focus move on load).
  void renderRoute(new URL(window.location.href), new AbortController().signal, { initial: true });
}

// Boot the router; call once from main.js after the shell markup exists.
export function startRouter() {
  startNavigationApi();
}

// Routes through the Navigation API, so it surfaces as the same `navigate` event our
// handler already owns.
export function navigateTo(url) {
  if (!window.navigation) {
    throw new Error("Router: Navigation API unavailable.");
  }
  window.navigation.navigate(url);
}

// A history TRAVERSE, so it returns the user to wherever they actually came from
// (Schedule or My Agenda) and the platform restores that view's scroll. Used by the
// session detail's "Back" link so it behaves like the browser back button rather than
// an unconditional push to /. When there's no same-document entry to go back to (a deep
// link or a fresh tab), fall back to a push to fallbackUrl instead of stranding the user.
export function navigateBack(fallbackUrl = "/") {
  const navigation = window.navigation;
  if (!navigation) {
    throw new Error("Router: Navigation API unavailable.");
  }
  if (navigation.canGoBack) {
    navigation.back();
  } else {
    navigation.navigate(fallbackUrl);
  }
}
